import { useQuery } from '@tanstack/react-query';

import { ServerGroupStatus } from '../types/tecton_proto/common/server_group_status';
import { ServerGroupType } from '../types/tecton_proto/common/server_group_type';
import {
  ListIngestServerGroupsRequest,
  ListServerGroupsRequest,
  ListServerGroupsResponse,
  ListTransformServerGroupsRequest,
  ServerGroupInfo,
  TransformServerGroup,
  IngestServerGroup,
} from '../types/tecton_proto/servergroupservice/server_group_service';
import { Tecton_Api, Server_Group_Service } from './index';
import { snakeCase } from 'lodash';
import { convertKeysToSnakeCase, deepConvertKeysToSnakeCase } from './utils';

export interface ServerGroup {
  serverGroupId?: string;
  name?: string;
  owner?: string;
  description?: string;
  status?: ServerGroupStatus;
  environment?: string;
  type?: ServerGroupType;
}

const fetchListServerGroups = async (args: ListServerGroupsRequest): Promise<ListServerGroupsResponse> => {
  const response = await Server_Group_Service.post(`list-server-groups`, args);
  return response.data.server_groups?.map((group: any) => {
    return convertKeysToSnakeCase(group);
  });
};

export const useListServerGroups = (workspace: string) => {
  const payload: ListServerGroupsRequest = {
    workspace: workspace,
  };

  return useQuery(['list-server-groups', workspace], () => {
    return fetchListServerGroups(payload);
  });
};

const fetchIngestServerGroups = async (
  args: ListIngestServerGroupsRequest
): Promise<{ ingestServerGroups: IngestServerGroup[] }> => {
  const response = await Tecton_Api.get(`ingest-server-groups`, { params: args });
  return {
    ingestServerGroups:
      response.data.ingestServerGroups?.map((group: any) => {
        return convertKeysToSnakeCase(group);
      }) ?? [],
  };
};

export const useGetIngestServerGroups = (workspace: string) => {
  const payload = {
    workspace: workspace,
  };

  return useQuery(['ingest-server-groups', workspace], () => {
    return fetchIngestServerGroups(payload);
  });
};

const fetchTransformServerGroups = async (
  args: ListTransformServerGroupsRequest
): Promise<{ transformServerGroups: TransformServerGroup[] }> => {
  const response = await Tecton_Api.get(`transform-server-groups`, { params: args });

  return {
    transformServerGroups: response.data.transformServerGroups?.map((group: [key: string, value: any]) => {
      return convertKeysToSnakeCase(group);
    }),
  };
};

export const useGetTransformServerGroups = (workspace: string) => {
  const payload: ListTransformServerGroupsRequest = {
    workspace: workspace,
  };

  return useQuery(['transform-server-groups', workspace], () => {
    return fetchTransformServerGroups(payload);
  });
};

const fetchTransformServerGroup = async (workspace: string, id: string): Promise<TransformServerGroup> => {
  const response = await Tecton_Api.get(`transform-server-groups/${id}`, { params: { workspace } });
  return convertKeysToSnakeCase(response.data);
};

export const useGetTransformServerGroup = (workspace: string, id: string) => {
  return useQuery(['transform-server-group', workspace, id], () => {
    return fetchTransformServerGroup(workspace, id);
  });
};

const fetchIngestServerGroup = async (workspace: string, id: string): Promise<IngestServerGroup> => {
  const response = await Tecton_Api.get(`ingest-server-groups/${id}`, { params: { workspace } });
  return deepConvertKeysToSnakeCase(Object.entries(response.data)) as IngestServerGroup;
};

export const useGetIngestServerGroup = (workspace: string, id: string) => {
  return useQuery(['ingest-server-group', workspace, id], () => {
    return fetchIngestServerGroup(workspace, id);
  });
};
